// src/edu-world/systems/session-manager.ts
// Session Manager — controls play session pacing and positive stopping points
// Standalone module (Phase 3) — not wired into orchestrator yet

// ─── Types ───

export type SessionPhase = 'active' | 'winding_down' | 'farewell';

export type SuggestedAction =
  | { type: 'continue'; reason: string }
  | { type: 'ensure_interactive_element'; reason: string }
  | { type: 'suggest_winding_down'; reason: string }
  | { type: 'suggest_farewell'; reason: string };

export interface SessionState {
  startTime: number;
  missionsCompleted: number;
  lastInteractionTime: number;
  phase: SessionPhase;
  totalInteractions: number;
}

export interface SessionManagerConfig {
  targetDurationMinMs: number; // 8 minutes default
  targetDurationMaxMs: number; // 12 minutes default
  idealMissions: number; // 2 missions ideal
  maxMissionsBeforeFarewell: number; // 3 missions triggers farewell
  maxWalkingWithoutInteractionMs: number; // 8 seconds
  windingDownMissionThreshold: number; // 2 missions triggers winding down
}

// ─── Default Configuration ───

const DEFAULT_CONFIG: SessionManagerConfig = {
  targetDurationMinMs: 8 * 60 * 1000, // 8 minutes
  targetDurationMaxMs: 12 * 60 * 1000, // 12 minutes
  idealMissions: 2,
  maxMissionsBeforeFarewell: 3,
  maxWalkingWithoutInteractionMs: 8000, // 8 seconds
  windingDownMissionThreshold: 2,
};

// ─── SessionManager ───

export class SessionManager {
  private state: SessionState | null = null;
  private config: SessionManagerConfig;

  constructor(config?: Partial<SessionManagerConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Start a new play session. Resets all session tracking.
   */
  startSession(): void {
    const now = Date.now();
    this.state = {
      startTime: now,
      missionsCompleted: 0,
      lastInteractionTime: now,
      phase: 'active',
      totalInteractions: 0,
    };
  }

  /**
   * Record a mission completion. Updates phase based on mission count.
   */
  recordMissionComplete(): void {
    if (!this.state) return;

    this.state.missionsCompleted++;
    this.state.lastInteractionTime = Date.now();
    this.state.totalInteractions++;

    this.updatePhase();
  }

  /**
   * Record any interaction (NPC dialogue, micro-interaction, etc.).
   * Used to track walking gaps.
   */
  recordInteraction(): void {
    if (!this.state) return;

    this.state.lastInteractionTime = Date.now();
    this.state.totalInteractions++;
  }

  /**
   * Get the current session phase.
   */
  getSessionPhase(): SessionPhase {
    if (!this.state) return 'active';

    // Re-evaluate phase based on current time
    this.updatePhase();
    return this.state.phase;
  }

  /**
   * Get a suggested action based on current session state.
   * Used by the world engine to decide what to surface next.
   */
  getSuggestedAction(): SuggestedAction {
    if (!this.state) {
      return { type: 'continue', reason: 'No active session' };
    }

    const now = Date.now();
    const elapsed = now - this.state.startTime;
    const timeSinceInteraction = now - this.state.lastInteractionTime;

    // Check if walking too long without interactive element
    if (timeSinceInteraction > this.config.maxWalkingWithoutInteractionMs) {
      return {
        type: 'ensure_interactive_element',
        reason: `${Math.round(timeSinceInteraction / 1000)}s without interaction, place something nearby`,
      };
    }

    // Check if session exceeds max duration or max missions
    if (
      elapsed >= this.config.targetDurationMaxMs ||
      this.state.missionsCompleted >= this.config.maxMissionsBeforeFarewell
    ) {
      return {
        type: 'suggest_farewell',
        reason: this.state.missionsCompleted >= this.config.maxMissionsBeforeFarewell
          ? `Completed ${this.state.missionsCompleted} missions`
          : `Session exceeds ${Math.round(this.config.targetDurationMaxMs / 60000)} minutes`,
      };
    }

    // Check if winding down threshold met
    if (this.state.missionsCompleted >= this.config.windingDownMissionThreshold) {
      return {
        type: 'suggest_winding_down',
        reason: `Completed ${this.state.missionsCompleted} missions (ideal target reached)`,
      };
    }

    return { type: 'continue', reason: 'Session progressing normally' };
  }

  /**
   * Get the current session state (for inspection/serialization).
   */
  getSessionState(): SessionState | null {
    return this.state ? { ...this.state } : null;
  }

  /**
   * Get elapsed session time in milliseconds.
   */
  getElapsedMs(): number {
    if (!this.state) return 0;
    return Date.now() - this.state.startTime;
  }

  /**
   * Get time since last interaction in milliseconds.
   */
  getTimeSinceLastInteractionMs(): number {
    if (!this.state) return 0;
    return Date.now() - this.state.lastInteractionTime;
  }

  /**
   * Check if the system should suggest a break to the child.
   * Returns true when winding-down or farewell phase is appropriate.
   * This is a suggestion only — never forced.
   */
  shouldSuggestBreak(): boolean {
    if (!this.state) return false;

    this.updatePhase();

    return this.state.phase === 'winding_down' || this.state.phase === 'farewell';
  }

  /**
   * Check if session is active.
   */
  isActive(): boolean {
    return this.state !== null;
  }

  // ─── Private ───

  private updatePhase(): void {
    if (!this.state) return;

    const elapsed = Date.now() - this.state.startTime;

    // Farewell: 3+ missions OR exceeded 12 minutes
    if (
      this.state.missionsCompleted >= this.config.maxMissionsBeforeFarewell ||
      elapsed >= this.config.targetDurationMaxMs
    ) {
      this.state.phase = 'farewell';
      return;
    }

    // Winding down: 2+ missions completed
    if (this.state.missionsCompleted >= this.config.windingDownMissionThreshold) {
      this.state.phase = 'winding_down';
      return;
    }

    this.state.phase = 'active';
  }
}
